import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export type AppRole = "broker" | "carrier" | "shipper" | "broker_carrier";

interface RoleGateProps {
  allow: AppRole[];
  children: React.ReactNode;
}

/**
 * Hard-gate a route by user_type. Admins always bypass.
 * Fail-closed: missing/unknown user_type does NOT default to broker — it redirects to /auth.
 * Unauthorized roles are redirected to their role-specific safe home.
 */
export const RoleGate: React.FC<RoleGateProps> = ({ allow, children }) => {
  const { profile, loading, isAdmin, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Admin bypass — intentional.
  if (isAdmin) return <>{children}</>;

  // Not signed in — kick to auth.
  if (!user) return <Navigate to="/auth" replace />;

  const rawType = profile?.user_type as AppRole | undefined;
  const validRoles: AppRole[] = ["broker", "carrier", "shipper", "broker_carrier"];

  // Fail-closed: missing or unrecognized user_type → no access.
  if (!rawType || !validRoles.includes(rawType)) {
    return <Navigate to="/auth" replace />;
  }

  if (allow.includes(rawType)) return <>{children}</>;

  // Role-specific safe fallback.
  const fallback =
    rawType === "shipper" ? "/shipper-dashboard" :
    rawType === "carrier" ? "/carrier-dashboard" :
    "/broker-dashboard"; // broker, broker_carrier
  return <Navigate to={fallback} replace />;
};

export default RoleGate;
